import { Box, styled, Tooltip } from "@mui/material";
import { ColumnsPanelTrigger, DataGridPro, DataGridProProps, Toolbar, ToolbarButton } from "@mui/x-data-grid-pro";
import { AppContext } from "../contexts/app_context/app_context";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useColumnSelectionConfig } from "../hooks/use_column_selection_config";

import ViewColumnIcon from "@mui/icons-material/ViewColumn";

/**
 * cssToPixels
 */

function cssToPixels(
  value: string,
  context: HTMLElement = document.documentElement
): number {
  // Cria um elemento temporário
  const temp = document.createElement("div");

  // Aplica o valor CSS
  temp.style.width = value;

  // Esconde o elemento
  temp.style.position = "absolute";
  temp.style.visibility = "hidden";

  // Adiciona ao contexto
  context.appendChild(temp);

  // Obtém o valor em pixels
  const pixels = parseFloat(getComputedStyle(temp).width);

  // Remove o elemento
  context.removeChild(temp);

  return pixels;
}

/**
 * CustomToolbar
 */

const CustomToolbar = ({ toolbarRightSlot }: any) => {
  return (
    <Toolbar>
      <Tooltip title="Colunas">
        <ColumnsPanelTrigger render={<ToolbarButton />}>
          <ViewColumnIcon fontSize="small" />
        </ColumnsPanelTrigger>
      </Tooltip>
    </Toolbar>
  );
};

/**
 * StyledDataGrid
 */

type textSize = "muitoPequeno" | "pequeno" | "medio" | "grande";

const tamanhoConfig: Record<textSize, { fontSize: string; }> = {
  muitoPequeno: { fontSize: "0.70rem" },
  pequeno: { fontSize: "0.75rem" },
  medio: { fontSize: "0.875rem" },
  grande: { fontSize: "1.2rem" },
};

interface Props extends DataGridProProps {
  tableName: string;
  autoHeight?: boolean;
  height?: string;
  textSize?: textSize;
  disableOutlineOnClick?: boolean;
}

export const StyledDataGrid = ({
  tableName,
  autoHeight,
  height = "80vh",
  disableRowSelectionExcludeModel = true,
  textSize = "medio",
  disableOutlineOnClick,
  ...props
}: Props) => {
  const containerRef = useRef<any>(null);
  const styleDisableOutlineOnClick = useMemo(() => {
    return disableOutlineOnClick === true
      ? { outline: "none !important" }
      : undefined;
  }, [
    disableOutlineOnClick,
  ]);

  const maxTableHeight = useMemo(() => cssToPixels(height), [height]);

  const [autoHeightState, setAutoHeightState] = useState(autoHeight);

  const [columnsVisibilityModel, handleColumnsVisibilityModel] = useColumnSelectionConfig({
    tabela: tableName,
  });

  const mode = useContext(AppContext).theme;
  const config = tamanhoConfig[textSize];
  const StyledDataGrid = useMemo(() => {
    return styled(DataGridPro)(({ theme }) => ({
      "& .MuiDataGrid-columnHeader": {
        backgroundColor: "#aeeece",
        fontSize: config.fontSize,
      },
      "& .MuiDataGrid-columnSeparator": {
        color: "black",
      },
      "& .MuiDataGrid-footerContainer": {
        display: "none",
      },
      "& .MuiDataGrid-toolbar .MuiIconButton-root": {
        color: mode === "light" ? "black" : "white",
      },
      "& .MuiDataGrid-columnHeaderTitleContainerContent": {
        "& .MuiCheckbox-root": {
          color: "black",
        },
      },
      "& .MuiIconButton-root": {
        color: "black",
      },
      "& .MuiDataGrid-columnHeaderTitle": {
        wordBreak: "normal",
        whiteSpace: "normal",
        lineHeight: "auto",
        color: "black",
        fontSize: config.fontSize,
      },
      "& .MuiDataGrid-cell": {
        fontSize: config.fontSize,
      },
      "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": styleDisableOutlineOnClick,
      backgroundColor: mode === "dark" ? "#2C2C2C" : "#FFFFFF",
    }));
  }, [styleDisableOutlineOnClick, mode, config]);

  useEffect(() => {
    if (autoHeight !== true) return;

    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      if (entries && entries[0] && entries[0].contentRect) {
        const { height: containerHeight } = entries[0].contentRect;
        setAutoHeightState(containerHeight < maxTableHeight - 10);
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, [
    autoHeight,
    maxTableHeight,
  ]);

  return (
    <Box
      ref={containerRef}
      sx={{ display: "flex", height: autoHeight === true ? (autoHeightState ? "auto" : height) : height, width: "100%" }}>
      <StyledDataGrid
        showToolbar={true}
        slots={{
          toolbar: CustomToolbar,
        }}
        slotProps={{
          columnsPanel: {
            style: {
              color: mode === "dark" ? "#ffffff" : "#000000",
            },
          },
        }}
        columnVisibilityModel={columnsVisibilityModel}
        disableColumnSorting={true}
        disableRowSelectionExcludeModel={disableRowSelectionExcludeModel}
        multipleColumnsSortingMode="always"
        onColumnVisibilityModelChange={handleColumnsVisibilityModel}
        {...props}
        autoHeight={autoHeightState}
        disableColumnMenu={false}
        localeText={{
          noRowsLabel: "Não existem itens para mostrar!",

          columnsManagementSearchTitle: "Buscar",
          columnsManagementNoColumns: "Não há colunas",
          columnsManagementShowHideAllText: "Exibir/Esconder",
          columnsManagementReset: "Reiniciar",
          columnsManagementDeleteIconLabel: "Limpar",

          pinToLeft: "Fixar a esquerda",
          pinToRight: "Fixar a direita",
          unpin: "Desfixar",

          columnMenuShowColumns: "Exibir colunas",
          columnMenuManageColumns: "Gerenciar colunas",

          columnMenuHideColumn: "Esconder coluna",
          columnMenuUnsort: "Desordenar",
          columnMenuSortAsc: "Ordenar ASC",
          columnMenuSortDesc: "Ordenar DESC",
        }}
      />
    </Box>
  );
};

